import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { Loader } from '@components/common';
import { OfficerService, JobService } from '@services';
import utils from '@utils';

import './RatingModal.scss';

class RatingModal extends Component {
	constructor(props) {
		super(props);

		this.initialState = {
			show          : false,
			loadingData   : false,
			loadingSubmit : false,
			officerName   : 'John Doe',
			clockInDate   : null,
			JobId         : null,
			OfficerId     : null,
			Rating        : 0,
			Feedback      : '',
			validations   : {
				Rating : false, // true = has error
			},
		};

		this.state = { ...this.initialState };
	}

	getOfficerRating = ({ JobId, OfficerId }) => {
		OfficerService.checkOfficerRating({ JobId, OfficerId })
			.then(({ data }) => {
				// Only show the modal if the rating does not exist
				if (data && data.length === 0) {
					this.setState({
						show : true,
						JobId,
						OfficerId,
					}, () => {
						this.getJobPositionData();
					});
				}
			})
			.catch(({ response }) => {
				this.setState({ loadingData : false });
				const errorMessage = response && response.data ? response.data.Message : 'Error getting officer rating';

				utils.notify({
					type    : 'error',
					message : errorMessage,
				});
			});
	}

	getJobPositionData = () => {
		const { JobId, OfficerId } = this.state;
		const { timeZoneId } = this.props;

		this.setState({ loadingData : true });

		// Call assigned officers by position id endpoint to get the officer name and clock in date
		JobService.getAssignedOfficersByPositionId(JobId)
			.then(({ data }) => {
				if (data && data.AssignedOfficers && data.AssignedOfficers.length > 0) {
					const officerIdInteger = parseInt(OfficerId, 10);
					const officer = data.AssignedOfficers.find((item) => item.UserId === officerIdInteger);

					this.setState({
						officerName : `${officer.OfficerProfile.FirstName} ${officer.OfficerProfile.LastName}`,
						clockInDate : officer.ClockIn ? utils.parseUTCDateToTimeZone(officer.ClockIn, timeZoneId).format('MM/DD/YYYY') : null,
						loadingData : false,
					});
				}
			})
			.catch(({ response }) => {
				this.setState({ loadingData : false });
				const errorMessage = response && response.data ? response.data.Message : 'Error getting position data';

				utils.notify({
					type    : 'error',
					message : errorMessage,
				});
			});
	}

	openModal = ({ JobId, OfficerId }) => {
		// First check if the rating already exists
		this.getOfficerRating({ JobId, OfficerId });
	}

	resetModal = () => {
		this.setState(this.initialState);
	}

	rate = (points) => () => {
		this.setState((state) => ({
			Rating      : points,
			validations : { ...state.validations, Rating : false },
		}));
	}

	rateWithKey = (points) => ({ key }) => {
		if (key === 'Enter') {
			this.rate(points)();
		}
	}

	onSubmit = () => {
		const {
			JobId, OfficerId, Rating, Feedback,
		} = this.state;

		// Validate form
		const ratingError = Rating === 0;

		// Only Rating is mandatory
		if (ratingError) {
			this.setState({
				validations : {
					Rating : ratingError,
				},
			});

			return;
		}

		this.setState({ loadingSubmit : true });

		OfficerService.saveOfficerRating({
			JobId,
			OfficerId,
			Rating,
			Feedback,
		})
			.then(({ data }) => {
				utils.notify({
					type    : 'success',
					message : data.Message || 'Rating saved successfully',
				});

				this.resetModal();
			})
			.catch(({ response }) => {
				this.setState({ loadingSubmit : false });
				const errorMessage = response && response.data ? response.data.Message : 'Error saving officer rating';

				utils.notify({
					type    : 'error',
					message : errorMessage,
				});
			});
	}

	onChangeForm = (event) => {
		this.setState({ Feedback : event.target.value });
	}

	renderStar = (value) => {
		const { Rating, validations } = this.state;

		return (
			<i
				className={`rkf rkf-star-${Rating > (value - 1) ? 'filled' : 'empty'} ${validations.Rating ? 'has-error' : ''}`}
				role="button"
				tabIndex="0"
				onClick={this.rate(value)}
				onKeyPress={this.rateWithKey(value)}
				aria-label={`Rate ${value} star`}
			/>
		);
	}

	render() {
		const {
			show, Rating, Feedback, officerName, clockInDate, loadingData, loadingSubmit,
		} = this.state;

		return (
			<Modal
				show={show}
				onExited={this.onExited}
				onHide={this.onCancel}
				id="rating-modal"
			>
				<Modal.Body className="text-center">
					<button
						type="button"
						className="btn btn-link"
						onClick={this.resetModal}
						id="rating-modal-close-btn"
					>
						<i className="rkf rkf-cross" />
					</button>
					<h2><strong>Rate your Officer</strong></h2>
					<br />
					<div id="rating-modal-officer-message">
						{`Let us know how satisfied you are with officer ${officerName} on your job performed ${clockInDate ? ` on ${clockInDate}` : ''}`}
						<Loader active={loadingData} />
					</div>
					<br />
					<div id="rating-modal-stars">
						{this.renderStar(1)}
						{this.renderStar(2)}
						{this.renderStar(3)}
						{this.renderStar(4)}
						{this.renderStar(5)}
					</div>
					<br />
					<div id="rating-modal-feedback-message">
						<strong>We love feedback!</strong>
					</div>
					<div className="form-group">
						<textarea
							rows="4"
							className="form-control"
							value={Feedback}
							onChange={this.onChangeForm}
						/>
					</div>
					<br />
					<div>
						<button
							type="button"
							className="btn btn-primary"
							onClick={this.onSubmit}
							id="rating-modal-submit-btn"
							disabled={loadingData || loadingSubmit || Rating === 0}
						>
							{loadingSubmit ? 'Loading' : 'Submit'}
						</button>
					</div>
					<br />
					<div id="rating-modal-privacy-message">
						Your ratings and comments are for internal use. They will not be directly shared with Officers.
					</div>
				</Modal.Body>
			</Modal>
		);
	}
}

RatingModal.propTypes = {
	timeZoneId : PropTypes.string,
};

RatingModal.defaultProps = {
	timeZoneId : '',
};

export default RatingModal;
